import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tab, Tabs } from "react-bootstrap";
import { Localization, SessionContext, SettingsManager, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { KeyValueTable } from "src/propertyInfoTemplates/PropertyComponents";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { createDefaultPLAProject, getMyPLAAccessRights, notifyPLAProjectsChanged, PLAElementRecord, PLAProject, PLARelationRecord, PLAUserAccessRights, PLAValueType } from "./PLAState";
import { PLAProjectEditor } from "src/modules/PLAModule/PLAProjectEditor";
import { PLADataServiceImpl, PLAProjectServiceImpl } from "./PLAPersistence";
import { collectPLAProjectExpressionIssues, formatPLAProjectExpressionIssue } from "./PLAProjectValidation";
import { OptimisticLockingConflictError } from "src/common/managers/ESVersionedPersistance";
import { createPLAExpressionMigrationPlan, PLAExpressionMigrationPlan, savePLAExpressionMigration, verifyPLAExpressionMigration } from "./PLAExpressionMigration";

type Props = {
   managedWindow: ManagedWindow;
   onFormClose: () => void;
};

type PLAProjectDefinitions = Pick<PLAProject, "settings" | "elementTypes" | "relationTypes" | "mainHierarchicalRelationTypeId">;
type PendingDefinitionImport = { project: PLAProject; definitions: PLAProjectDefinitions };
type CopyFromProjectState = { targetProject: PLAProject; sourceProjectId: string; copying: boolean };

const PLAProjectDefinitionsFileType = "PLAProjectDefinitions";
const PLAProjectDefinitionsFileVersion = 1;
const PLAValueTypes: PLAValueType[] = ["string", "number", "m2", "stk", "kr", "date", "boolean"];

function isRecord(value: unknown): value is Record<string, any> {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function downloadBlob(fileName: string, blob: Blob) {
   const url = URL.createObjectURL(blob);
   const link = document.createElement("a");
   link.className = "mit-download-link";
   link.download = fileName;
   link.href = url;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);
}

function safeFileName(value: string) {
   return (value || "pla-project").replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "") || "pla-project";
}

function createDefinitionsExport(project: PLAProject) {
   return {
      fileType: PLAProjectDefinitionsFileType,
      version: PLAProjectDefinitionsFileVersion,
      exportedAt: new Date().toISOString(),
      projectId: project.id,
      projectName: project.name,
      definitions: {
         settings: project.settings || {},
         elementTypes: project.elementTypes || {},
         relationTypes: project.relationTypes || {},
         mainHierarchicalRelationTypeId: project.mainHierarchicalRelationTypeId
      } satisfies PLAProjectDefinitions
   };
}

function hasMainHierarchicalRelationTypeDefinition(definitions: Partial<PLAProjectDefinitions>) {
   return Object.prototype.hasOwnProperty.call(definitions, "mainHierarchicalRelationTypeId");
}

function validateProjectDefinitionsFile(value: unknown, targetProject: PLAProject): { definitions?: PLAProjectDefinitions; errors: string[] } {
   const errors: string[] = [];
   if (!isRecord(value)) return { errors: [Localization.getText("Invalid PLA project definitions file")] };
   if (value.fileType !== PLAProjectDefinitionsFileType) errors.push(Localization.getText("Invalid PLA project definitions file"));
   if (value.version !== PLAProjectDefinitionsFileVersion) errors.push(Localization.getText("Unsupported PLA project definitions file version"));
   if (!isRecord(value.definitions)) errors.push(Localization.getText("Missing project definitions"));
   if (errors.length > 0 || !isRecord(value.definitions)) return { errors };

   const definitions = value.definitions as Partial<PLAProjectDefinitions>;
   if (!isRecord(definitions.settings)) errors.push(Localization.getText("Project settings must be an object"));
   if (!isRecord(definitions.elementTypes)) errors.push(Localization.getText("Element types must be an object"));
   if (!isRecord(definitions.relationTypes)) errors.push(Localization.getText("Relation types must be an object"));
   if (definitions.mainHierarchicalRelationTypeId !== undefined && typeof definitions.mainHierarchicalRelationTypeId !== "string") errors.push(Localization.getText("Main hierarchical relation must be a relation type"));
   if (errors.length > 0 || !isRecord(definitions.settings) || !isRecord(definitions.elementTypes) || !isRecord(definitions.relationTypes)) return { errors };

   for (const [relationTypeId, relationType] of Object.entries(definitions.relationTypes)) {
      if (!relationTypeId.trim()) errors.push(Localization.getText("Relation type id is required"));
      if (!isRecord(relationType) || typeof relationType.name !== "string") errors.push(`${Localization.getText("Invalid relation type")}: ${relationTypeId}`);
   }

   const validationProject: PLAProject = {
      ...targetProject,
      settings: definitions.settings,
      elementTypes: definitions.elementTypes,
      relationTypes: definitions.relationTypes,
      ...(hasMainHierarchicalRelationTypeDefinition(definitions) ? { mainHierarchicalRelationTypeId: definitions.mainHierarchicalRelationTypeId } : {})
   };

   if (definitions.mainHierarchicalRelationTypeId && !definitions.relationTypes[definitions.mainHierarchicalRelationTypeId]) errors.push(Localization.getText("Main hierarchical relation must be a relation type"));

   for (const [elementTypeId, elementType] of Object.entries(definitions.elementTypes)) {
      if (!elementTypeId.trim()) errors.push(Localization.getText("Element type id is required"));
      if (!isRecord(elementType)) {
         errors.push(`${Localization.getText("Invalid element type")}: ${elementTypeId}`);
         continue;
      }
      if (typeof elementType.name !== "string") errors.push(`${Localization.getText("Element type name is required")}: ${elementTypeId}`);
      if (typeof elementType.sortOrder !== "number" || !Number.isFinite(elementType.sortOrder)) errors.push(`${Localization.getText("Element type sort order must be a number")}: ${elementTypeId}`);
      if (elementType.color !== undefined && typeof elementType.color !== "string") errors.push(`${Localization.getText("Element type color must be a string")}: ${elementTypeId}`);
      if (!isRecord(elementType.attributes)) {
         errors.push(`${Localization.getText("Element type attributes must be an object")}: ${elementTypeId}`);
         continue;
      }

      for (const [attributeId, attribute] of Object.entries(elementType.attributes)) {
         if (!attributeId.trim()) errors.push(`${Localization.getText("Attribute id is required")}: ${elementTypeId}`);
         if (!isRecord(attribute)) {
            errors.push(`${Localization.getText("Invalid attribute")}: ${elementTypeId}.${attributeId}`);
            continue;
         }
         if (typeof attribute.name !== "string") errors.push(`${Localization.getText("Attribute name is required")}: ${elementTypeId}.${attributeId}`);
         if (attribute.description !== undefined && typeof attribute.description !== "string") errors.push(`${Localization.getText("Attribute description must be a string")}: ${elementTypeId}.${attributeId}`);
         if (attribute.type !== "constant" && attribute.type !== "expression" && attribute.type !== "cube") errors.push(`${Localization.getText("Invalid attribute type")}: ${elementTypeId}.${attributeId}`);
         if (attribute.valueType !== undefined && !PLAValueTypes.includes(attribute.valueType)) errors.push(`${Localization.getText("Invalid value type")}: ${elementTypeId}.${attributeId}`);
         if (attribute.sortOrder !== undefined && (typeof attribute.sortOrder !== "number" || !Number.isFinite(attribute.sortOrder))) errors.push(`${Localization.getText("Attribute sort order must be a number")}: ${elementTypeId}.${attributeId}`);

         if (attribute.type === "expression" && typeof attribute.expression !== "string") errors.push(`${Localization.getText("Expression is required")}: ${elementType.name || elementTypeId} > ${attribute.name || attributeId}`);

         if (attribute.alerts !== undefined) {
            if (!isRecord(attribute.alerts)) {
               errors.push(`${Localization.getText("Alerts must be an object")}: ${elementTypeId}.${attributeId}`);
            } else {
               for (const [alertId, alert] of Object.entries(attribute.alerts)) {
                  if (!isRecord(alert) || typeof alert.booleanExpression !== "string" || typeof alert.alertMessage !== "string" || (alert.optimizerMayOverride !== undefined && typeof alert.optimizerMayOverride !== "boolean")) {
                     errors.push(`${Localization.getText("Invalid alert")}: ${elementTypeId}.${attributeId}.${alertId}`);
                     continue;
                  }
               }
            }
         }
      }
   }

   if (errors.length === 0) errors.push(...collectPLAProjectExpressionIssues(validationProject).map((issue) => formatPLAProjectExpressionIssue(validationProject, issue)));

   return errors.length > 0
      ? { errors }
      : { definitions: definitions as PLAProjectDefinitions, errors: [] };
}

function validateProjectElementImportCompatibility(sourceProject: PLAProject, targetProject: PLAProject): string[] {
   const errors: string[] = [];
   const sourceRelationTypeIds = Object.keys(sourceProject.relationTypes || {}).sort();
   const targetRelationTypeIds = Object.keys(targetProject.relationTypes || {}).sort();
   if (sourceRelationTypeIds.join("\u0000") !== targetRelationTypeIds.join("\u0000")) errors.push(Localization.getText("Project relation types are not compatible"));
   const sourceElementTypes = sourceProject.elementTypes || {};
   const targetElementTypes = targetProject.elementTypes || {};
   const sourceElementTypeIds = Object.keys(sourceElementTypes).sort();
   const targetElementTypeIds = Object.keys(targetElementTypes).sort();
   if (sourceElementTypeIds.join("\u0000") !== targetElementTypeIds.join("\u0000")) errors.push(Localization.getText("Project element types are not compatible"));

   sourceElementTypeIds.forEach((elementTypeId) => {
      const sourceElementType = sourceElementTypes[elementTypeId];
      const targetElementType = targetElementTypes[elementTypeId];
      if (!targetElementType) return;
      const sourceAttributes = sourceElementType.attributes || {};
      const targetAttributes = targetElementType.attributes || {};
      const sourceAttributeIds = Object.keys(sourceAttributes).sort();
      const targetAttributeIds = Object.keys(targetAttributes).sort();
      if (sourceAttributeIds.join("\u0000") !== targetAttributeIds.join("\u0000")) errors.push(`${Localization.getText("Project element attributes are not compatible")}: ${sourceElementType.name || elementTypeId}`);
      sourceAttributeIds.forEach((attributeId) => {
         const sourceAttribute = sourceAttributes[attributeId];
         const targetAttribute = targetAttributes[attributeId];
         if (!targetAttribute) return;
         if (sourceAttribute.type !== targetAttribute.type || sourceAttribute.valueType !== targetAttribute.valueType) {
            errors.push(`${Localization.getText("Project element attribute types are not compatible")}: ${sourceElementType.name || elementTypeId}.${sourceAttribute.name || attributeId}`);
         }
      });
   });

   return Array.from(new Set(errors));
}

function copyPLAElementForProject(element: PLAElementRecord): PLAElementRecord {
   const { _version, ...elementWithoutVersion } = element;
   return elementWithoutVersion;
}

function copyPLARelationForProject(relation: PLARelationRecord): PLARelationRecord {
   const { _version, ...relationWithoutVersion } = relation;
   return relationWithoutVersion;
}

export function PLAAdminDialog(props: Props) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const [projects, setProjects] = useState<PLAProject[]>([]);
   const [showProjectEditor, setShowProjectEditor] = useState<PLAProject | null>(null);
   const [projectEditorRevision, setProjectEditorRevision] = useState(0);
   const [newProject, setNewProject] = useState(false);
   const [confirmDelete, setConfirmDelete] = useState<string | undefined>();
   const [pendingDefinitionImport, setPendingDefinitionImport] = useState<PendingDefinitionImport | undefined>();
   const [copyFromProject, setCopyFromProject] = useState<CopyFromProjectState | undefined>();
   const [expressionMigration, setExpressionMigration] = useState<{ plan: PLAExpressionMigrationPlan; warnings: string[] }>();
   const [migratingExpressions, setMigratingExpressions] = useState(false);

   const projectService = () => new PLAProjectServiceImpl(SettingsManager.getSystemSetting("pla.projectsElasticIndex", ""), sessionState.userName || sessionState.userRef);
   const dataService = (index: string) => new PLADataServiceImpl(index, sessionState.userName || sessionState.userRef);

   const applyFreshProject = (freshProject: PLAProject) => {
      setProjects((prev) => prev.some((item) => item.id === freshProject.id) ? prev.map((item) => item.id === freshProject.id ? freshProject : item) : [...prev, freshProject]);
      if (showProjectEditor?.id === freshProject.id) setProjectEditorRevision((revision) => revision + 1);
      setShowProjectEditor((current) => current?.id === freshProject.id ? freshProject : current);
      setPendingDefinitionImport((current) => current?.project.id === freshProject.id ? undefined : current);
      notifyPLAProjectsChanged();
   };

   const loadProjects = (preferredProject?: PLAProject) => {
      projectService().loadProjects()
         .then((loadedProjects) => {
            if (!preferredProject) {
               setProjects(loadedProjects);
               return;
            }
            const exists = loadedProjects.some((item) => item.id === preferredProject.id);
            setProjects(exists ? loadedProjects.map((item) => item.id === preferredProject.id ? preferredProject : item) : [...loadedProjects, preferredProject]);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load projects")}: ${error.message || error}`)));
   };

   useEffect(() => {
      loadProjects();
   }, [sessionState.userName]);

   function canAdminProject(project: PLAProject): boolean {
      if (hasAccessToFeature(Feature.PLAToolGlobalAdmin)) return true;
      return getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).includes(PLAUserAccessRights.Admin);
   }

   const previewExpressionMigration = async (project: PLAProject) => {
      if (!hasAccessToFeature(Feature.PLAToolGlobalAdmin)) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Global PLA administrator access is required")));
         return;
      }
      setMigratingExpressions(true);
      try {
         const freshProject = await projectService().loadProject(project.id);
         const records = await dataService(freshProject.esIndexName).loadAllRecords();
         const elements = records.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
         const relations = records.filter((record): record is PLARelationRecord => record.recordType === "RELATION");
         const plan = createPLAExpressionMigrationPlan(freshProject, elements);
         const warnings = verifyPLAExpressionMigration(plan, relations);
         if (!plan.changes.length) appMessageDispatch(actionSetInfoMessage(Localization.getText("Expressions already use the new syntax")));
         else setExpressionMigration({ plan, warnings });
      } catch (error) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to migrate expressions")}: ${error instanceof Error ? error.message : String(error)}`));
      } finally {
         setMigratingExpressions(false);
      }
   };

   const confirmExpressionMigration = async () => {
      if (!expressionMigration) return;
      if (!hasAccessToFeature(Feature.PLAToolGlobalAdmin)) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Global PLA administrator access is required")));
         return;
      }
      setMigratingExpressions(true);
      try {
         const { plan } = expressionMigration;
         downloadBlob(`${safeFileName(plan.originalProject.name)}-expression-migration-backup.json`, new Blob([JSON.stringify({ project: plan.originalProject, elements: plan.originalElements }, null, 2)], { type: "application/json" }));
         const saved = await savePLAExpressionMigration(plan, projectService(), dataService(plan.project.esIndexName), () => hasAccessToFeature(Feature.PLAToolGlobalAdmin));
         applyFreshProject(saved);
         appMessageDispatch(actionSetInfoMessage(`${Localization.getText("Expressions migrated")}: ${plan.changes.length}`));
         setExpressionMigration(undefined);
      } catch (error) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to migrate expressions")}: ${error instanceof Error ? error.message : String(error)}`));
         setExpressionMigration(undefined);
         loadProjects();
         notifyPLAProjectsChanged();
      } finally {
         setMigratingExpressions(false);
      }
   };

   const handleSaveProject = async (project: PLAProject, createIndex = false) => {
      try {
         const projectsElasticIndex = SettingsManager.getSystemSetting("pla.projectsElasticIndex", "pla_projects_staging");
         const nextProject = { ...project, esIndexName: project.esIndexName || `${projectsElasticIndex}_${project.id}` };
         const expressionIssues = collectPLAProjectExpressionIssues(nextProject);
         if (expressionIssues.length > 0) {
            appMessageDispatch(actionSetErrorMessage(expressionIssues.map((issue) => formatPLAProjectExpressionIssue(nextProject, issue)).join("\n")));
            return;
         }
         if (createIndex) {
            await projectService().createProject(nextProject);
         } else {
            await projectService().saveProject(nextProject);
         }
         const savedProject = await projectService().loadProject(nextProject.id).catch(() => nextProject);
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Project saved")));
         setShowProjectEditor(null);
         setNewProject(false);
         setProjects((prev) => {
            const exists = prev.some((item) => item.id === savedProject.id);
            return exists ? prev.map((item) => item.id === savedProject.id ? savedProject : item) : [...prev, savedProject];
         });
         notifyPLAProjectsChanged();
         loadProjects(savedProject);
      } catch (error: any) {
         if (error instanceof OptimisticLockingConflictError) {
            const freshProject = error.currentDocument as PLAProject | undefined;
            if (freshProject?.id) {
               applyFreshProject(freshProject);
               loadProjects(freshProject);
            } else {
               loadProjects();
            }
            appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save project")}: ${Localization.getText("Project was changed by another user and has been reloaded")}`));
            return;
         }
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save project")}: ${error.message || error}`));
      }
   };

   const exportProjectDefinitions = (project: PLAProject) => {
      const blob = new Blob([JSON.stringify(createDefinitionsExport(project), null, 2)], { type: "application/json" });
      downloadBlob(`${safeFileName(project.name || project.id)}-pla-definitions.json`, blob);
   };

   const importProjectDefinitions = (project: PLAProject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "application/json,.json";
      input.onchange = async () => {
         const file = input.files?.[0];
         if (!file) return;
         try {
            const parsed = JSON.parse(await file.text());
            const validation = validateProjectDefinitionsFile(parsed, project);
            if (!validation.definitions || validation.errors.length > 0) {
               appMessageDispatch(actionSetErrorMessage(validation.errors.join("\n")));
               return;
            }
            setPendingDefinitionImport({ project, definitions: validation.definitions });
         } catch (error: any) {
            appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to import project definitions")}: ${error.message || error}`));
         }
      };
      input.click();
   };

   const confirmImportProjectDefinitions = () => {
      if (!pendingDefinitionImport) return;
      const nextProject: PLAProject = {
         ...pendingDefinitionImport.project,
         settings: pendingDefinitionImport.definitions.settings,
         elementTypes: pendingDefinitionImport.definitions.elementTypes,
         relationTypes: pendingDefinitionImport.definitions.relationTypes,
         ...(hasMainHierarchicalRelationTypeDefinition(pendingDefinitionImport.definitions) ? { mainHierarchicalRelationTypeId: pendingDefinitionImport.definitions.mainHierarchicalRelationTypeId } : {})
      };
      setPendingDefinitionImport(undefined);
      handleSaveProject(nextProject);
   };

   const copyElementsFromProject = async () => {
      if (!copyFromProject || copyFromProject.copying) return;
      const sourceProject = projects.find((project) => project.id === copyFromProject.sourceProjectId);
      if (!sourceProject) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Select a project to copy from")));
         return;
      }
      const compatibilityErrors = validateProjectElementImportCompatibility(sourceProject, copyFromProject.targetProject);
      if (compatibilityErrors.length > 0) {
         appMessageDispatch(actionSetErrorMessage(compatibilityErrors.join("\n")));
         return;
      }

      setCopyFromProject((current) => current ? { ...current, copying: true } : current);
      try {
         const sourceDataService = dataService(sourceProject.esIndexName);
         const targetDataService = dataService(copyFromProject.targetProject.esIndexName);
         const [sourceElements, sourceRelations, targetElements, targetRelations] = await Promise.all([
            sourceDataService.loadElements(),
            sourceDataService.loadRelations(),
            targetDataService.loadElements(),
            targetDataService.loadRelations()
         ]);
         const targetElementIds = new Set(targetElements.map((element) => element.id));
         const targetRelationIds = new Set(targetRelations.map((relation) => relation.id));
         const elementsToCopy = sourceElements.filter((element) => !targetElementIds.has(element.id));
         const targetElementIdsAfterCopy = new Set([...targetElementIds, ...elementsToCopy.map((element) => element.id)]);
         const relationsToCopy = sourceRelations.filter((relation) => !targetRelationIds.has(relation.id) && targetElementIdsAfterCopy.has(relation.fromId) && targetElementIdsAfterCopy.has(relation.toId));
         await Promise.all(elementsToCopy.map((element) => targetDataService.saveElement(copyPLAElementForProject(element))));
         await Promise.all(relationsToCopy.map((relation) => targetDataService.saveRelation(copyPLARelationForProject(relation))));
         appMessageDispatch(actionSetInfoMessage(`${Localization.getText("Copied records from project")}: ${sourceProject.name} (${elementsToCopy.length} ${Localization.getText("Elements")}, ${relationsToCopy.length} ${Localization.getText("Relations")})`));
         setCopyFromProject(undefined);
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to copy elements from project")}: ${error.message || error}`));
         setCopyFromProject((current) => current ? { ...current, copying: false } : current);
      }
   };

   return (
      <AdvancedDragModal
         PosDefault={{ width: "1000px", left: "50%", transform: "translateX(-50%)", top: "1rem" }}
         title={Localization.getText("PLA Database administration")}
         onClose={() => windowDispatch(closeWindow(WindowId.PLAAdminDialog))}
         allowUserMiniMize={true}
         window={props.managedWindow}
      >
         <ADModalBody>
            <Tabs defaultActiveKey="PLAProjects">
               <Tab eventKey="PLAProjects" title={Localization.getText("Projects")}>
                  <ProtectedFeature feature={Feature.PLAToolAdmin}>
                     <KeyValueTable>
                        <tbody>
                           <tr className="bold">
                              <td>{Localization.getText("Project")}</td>
                              <td>{Localization.getText("Protected")}</td>
                              <td>{Localization.getText("ESIndexName")}</td>
                              <td>{Localization.getText("Actions")}</td>
                           </tr>
                           {projects.filter(canAdminProject).map((project) => (
                              <tr key={project.id}>
                                 <td>{project.name}</td>
                                 <td>{project.protected ? Localization.getText("yes") : ""}</td>
                                 <td>{project.esIndexName}</td>
                                 <td>
                                    <GlassButtonRow>
                                       <GlassButton onClick={() => setShowProjectEditor(project)}>{Localization.getText("Edit")}</GlassButton>
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton onClick={() => exportProjectDefinitions(project)}>{Localization.getText("Export definitions")}</GlassButton>}
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton onClick={() => importProjectDefinitions(project)}>{Localization.getText("Import definitions")}</GlassButton>}
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton onClick={() => setCopyFromProject({ targetProject: project, sourceProjectId: projects.find((item) => item.id !== project.id)?.id || "", copying: false })}>{Localization.getText("Copy from project")}</GlassButton>}
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton disabled={migratingExpressions} onClick={() => previewExpressionMigration(project)}>{Localization.getText("Migrate expressions")}</GlassButton>}
                                       {!project.protected && <GlassButton onClick={() => setConfirmDelete(project.id)}>{Localization.getText("Delete")}</GlassButton>}
                                    </GlassButtonRow>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </KeyValueTable>
                     <GlassButtonRow>
                        <GlassButton onClick={() => loadProjects()}>{Localization.getText("Load projects")}</GlassButton>
                        <GlassButton onClick={() => setNewProject(true)}>{Localization.getText("New Project")}</GlassButton>
                     </GlassButtonRow>
                  </ProtectedFeature>
               </Tab>
            </Tabs>
         </ADModalBody>
         {showProjectEditor ? createPortal(
            <PLAProjectEditor key={`${showProjectEditor.id}:${projectEditorRevision}`} value={showProjectEditor} onClose={() => setShowProjectEditor(null)} onChange={(project) => handleSaveProject(project)} />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {newProject ? createPortal(
            <PLAProjectEditor title={Localization.getText("New Project")} value={createDefaultPLAProject(sessionState.userRef, sessionState.customerRef)} onClose={() => setNewProject(false)} onChange={(project) => handleSaveProject(project, true)} />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {confirmDelete ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Delete Project")}
               onSubmit={() => {
                  projectService().deleteProject(confirmDelete).then(() => loadProjects());
                  setConfirmDelete(undefined);
               }}
               onCancel={() => setConfirmDelete(undefined)}
            >
               {Localization.getText("Confirm deletion")}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {copyFromProject ? createPortal(
            <AdvancedDragModal title={Localization.getText("Copy from project")} PosDefault={{ width: "520px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={() => setCopyFromProject(undefined)}>
               <ADModalBody>
                  <KeyValueTable>
                     <tbody>
                        <tr>
                           <td>{Localization.getText("Project")}</td>
                           <td>{copyFromProject.targetProject.name}</td>
                        </tr>
                        <tr>
                           <td>{Localization.getText("Copy from project")}</td>
                           <td>
                              <select disabled={copyFromProject.copying} value={copyFromProject.sourceProjectId} onChange={(event) => setCopyFromProject((current) => current ? { ...current, sourceProjectId: event.target.value } : current)}>
                                 <option value="">{Localization.getText("Select a project to copy from")}</option>
                                 {projects.filter((project) => project.id !== copyFromProject.targetProject.id).sort((a, b) => a.name.localeCompare(b.name)).map((project) => (
                                    <option key={project.id} value={project.id}>{project.name}</option>
                                 ))}
                              </select>
                           </td>
                        </tr>
                     </tbody>
                  </KeyValueTable>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton disabled={copyFromProject.copying || !copyFromProject.sourceProjectId} onClick={copyElementsFromProject}>{Localization.getText("Copy from project")}</GlassButton>
                     <GlassButton disabled={copyFromProject.copying} onClick={() => setCopyFromProject(undefined)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {pendingDefinitionImport ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Import project definitions")}
               onSubmit={confirmImportProjectDefinitions}
               onCancel={() => setPendingDefinitionImport(undefined)}
            >
               {Localization.getText("Importing project definitions will overwrite the current project definitions. Continue?")}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {expressionMigration ? createPortal(
            <AdvancedDragModal title={Localization.getText("Migrate expressions")} PosDefault={{ width: "800px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={() => { if (!migratingExpressions) setExpressionMigration(undefined); }}>
               <ADModalBody>
                  <p>{expressionMigration.plan.project.name}: {expressionMigration.plan.changes.length} {Localization.getText("Expressions")}</p>
                  <p>{Localization.getText("Expression migration confirmation")}</p>
                  <div style={{ maxHeight: "50vh", overflow: "auto" }}>
                     {expressionMigration.plan.changes.map((change, index) => <div key={index} style={{ marginBottom: 12 }}>
                        <strong>{change.location}</strong>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{change.before}</pre>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{change.after}</pre>
                     </div>)}
                     {expressionMigration.warnings.map((warning) => <p key={warning} style={{ color: "#b36b00" }}>{warning}</p>)}
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow adjust="end">
                     <GlassButton disabled={migratingExpressions} onClick={confirmExpressionMigration}>{Localization.getText("Migrate expressions")}</GlassButton>
                     <GlassButton disabled={migratingExpressions} onClick={() => setExpressionMigration(undefined)}>{Localization.getText("Cancel")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
      </AdvancedDragModal>
   );
}
